<div class="view-container">
  <mat-card class="stocktake-location-live">
    <div class="stocktake-location-live__header">
      <div class="mbp-page-header">
        <div class="mbp-page-header__icon">
          <mat-icon>inventory_2</mat-icon>
        </div>
        <div>
          <h1 class="mbp-page-header__title">Live stock view</h1>
          @if (lastUpdated) {
            <p class="mbp-page-header__subtitle">Updated {{ lastUpdated | date : 'medium' }}</p>
          }
        </div>
      </div>

      <div class="stocktake-location-live__toolbar" [formGroup]="filtersForm">
        <mat-form-field appearance="outline" class="stocktake-location-live__search" subscriptSizing="dynamic">
          <input matInput formControlName="search" placeholder="Search stock lines" autocomplete="off" />
          <mat-icon matPrefix>search</mat-icon>
        </mat-form-field>

        <div class="stocktake-location-live__toolbar-actions">
          <mat-slide-toggle formControlName="autoRefresh" color="primary">
            Auto-refresh (30s)
          </mat-slide-toggle>

          <button
            mat-flat-button
            color="primary"
            type="button"
            class="stocktake-location-live__refresh-btn"
            [disabled]="loading"
            (click)="load()"
          >
            <mat-icon>refresh</mat-icon>
            Refresh
          </button>
        </div>
      </div>
    </div>

    @if (loadError) {
      <div class="stocktake-location-live__alert" role="alert">
        Could not load location data. Confirm the API implements paginated
        <code>GET /stocktake/location/live</code> and try again.
      </div>
    }

    <mat-paginator
      class="stocktake-location-live__paginator"
      [length]="totalCount"
      [pageIndex]="page - 1"
      [pageSize]="limit"
      [pageSizeOptions]="[10, 15, 20, 50]"
      showFirstLastButtons
      (page)="onPageChange($event)"
      aria-label="Select page of location stock lines"
    >
    </mat-paginator>

    <div class="stocktake-location-live__table-area">
      @if (loading) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }

      <div class="mbp-table-wrap">
        <table
          mat-table
          matSort
          [dataSource]="dataSource"
          class="stocktake-location-live__table mbp-data-table w-100"
          (matSortChange)="onSortChange($event)"
        >
          <ng-container matColumnDef="medicineName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="name-col">Medication</th>
            <td mat-cell *matCellDef="let row" class="name-col">{{ row.medicineName }}</td>
          </ng-container>

          <ng-container matColumnDef="itemLabel">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Item</th>
            <td mat-cell *matCellDef="let row">{{ row.itemLabel || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="strengthLabel">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Strength</th>
            <td mat-cell *matCellDef="let row">{{ row.strengthLabel || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="packagingLabel">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Packaging</th>
            <td mat-cell *matCellDef="let row">{{ row.packagingLabel || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="packSize">
            <th mat-header-cell *matHeaderCellDef>Pack size</th>
            <td mat-cell *matCellDef="let row">{{ packSizeDisplay(row) }}</td>
          </ng-container>

          <ng-container matColumnDef="onHand">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="col-qty"
              [matTooltip]="qtyHeaderTooltips.onHand"
              matTooltipShowDelay="300"
            >
              On Hand
            </th>
            <td
              mat-cell
              *matCellDef="let row"
              class="col-qty"
              [class.qty-positive]="row.onHand > 0"
            >
              {{ row.onHand }}
            </td>
          </ng-container>

          <ng-container matColumnDef="allocated">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="col-qty"
              [matTooltip]="qtyHeaderTooltips.allocated"
              matTooltipShowDelay="300"
            >
              Allocated
            </th>
            <td
              mat-cell
              *matCellDef="let row"
              class="col-qty"
              [class.qty-positive]="row.allocated > 0"
            >
              {{ row.allocated }}
            </td>
          </ng-container>

          <ng-container matColumnDef="onHold">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="col-qty"
              [matTooltip]="qtyHeaderTooltips.onHold"
              matTooltipShowDelay="300"
            >
              On hold
            </th>
            <td
              mat-cell
              *matCellDef="let row"
              class="col-qty"
              [class.qty-positive]="row.onHold > 0"
            >
              {{ row.onHold }}
            </td>
          </ng-container>

          <ng-container matColumnDef="total">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="col-qty"
              [matTooltip]="qtyHeaderTooltips.total"
              matTooltipShowDelay="300"
            >
              Total
            </th>
            <td
              mat-cell
              *matCellDef="let row"
              class="col-qty fw-bold"
              [class.qty-positive]="row.total > 0"
            >
              {{ row.total }}
            </td>
          </ng-container>

          <ng-container matColumnDef="lowStock">
            <th mat-header-cell *matHeaderCellDef class="col-qty col-low-stock">Low Stock</th>
            <td mat-cell *matCellDef="let row" class="col-qty col-low-stock">
              <span class="stocktake-location-live__low-stock-checkbox">
                <mat-checkbox
                  [checked]="row.lowStock"
                  [disableRipple]="true"
                  disabled
                  aria-label="Low stock flag"
                ></mat-checkbox>
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns; trackBy: trackByRow"
          ></tr>
        </table>
      </div>

      @if (!loading && dataSource.data.length === 0) {
        <div class="stocktake-location-live__empty">
          <mat-icon>inventory</mat-icon>
          <p>No rows match your search or the list is empty.</p>
        </div>
      }
    </div>
  </mat-card>
</div>
